import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { useAuth } from '@/store/auth/auth.store';
import { useDashboardSettingsQuery, useSaveDashboardSettingsMutation } from '@/store/dashboard';

export type ThemePreference = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

const DEFAULT_PREFERENCE: ThemePreference = 'dark';
const CACHE_KEY = 'app-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const isPreference = (value: unknown): value is ThemePreference =>
  value === 'light' || value === 'dark' || value === 'system';

const readCachedPreference = (): ThemePreference | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    return isPreference(cached) ? cached : null;
  } catch {
    return null;
  }
};

const resolve = (preference: ThemePreference): ResolvedTheme =>
  preference === 'system' ? (window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light') : preference;

export const applyThemePreference = (preference: ThemePreference) => {
  document.documentElement.setAttribute('data-theme', resolve(preference));
  try {
    localStorage.setItem(CACHE_KEY, preference);
  } catch (error) {
    console.error(error);
  }
};

export function useThemePreference() {
  const userId = useAuth((s) => s.session?.id);
  const queryClient = useQueryClient();
  const { data: settings, isSuccess } = useDashboardSettingsQuery(userId);
  const { mutate: save } = useSaveDashboardSettingsMutation(userId);

  const stored = settings?.theme;
  const preference: ThemePreference = isPreference(stored)
    ? stored
    : (readCachedPreference() ?? DEFAULT_PREFERENCE);

  useEffect(() => {
    applyThemePreference(preference);
    if (preference !== 'system') return;

    const media = window.matchMedia(DARK_QUERY);
    const onChange = () => applyThemePreference('system');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [preference]);

  const setPreference = useCallback(
    (next: ThemePreference) => {
      applyThemePreference(next);
      if (!isSuccess) return;
      const updated = { ...(settings ?? { dashboards: {} }), theme: next };
      queryClient.setQueryData(['settings', userId], updated);
      save(updated);
    },
    [isSuccess, settings, save, queryClient, userId],
  );

  return { preference, isLoaded: isSuccess, setPreference };
}
